import { type SxProps, type Theme } from '@mui/material'

export const styles = {
  panel: {
    backgroundColor: 'var(--grey-100)',
    p: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  } satisfies SxProps<Theme>,

  panelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px'
  } satisfies SxProps<Theme>,

  panelTitle: { fontWeight: 600 } satisfies SxProps<Theme>,

  card: {
    backgroundColor: 'var(--white)',
    border: '1px solid var(--border)',
    borderRadius: '6px',
    pt: '16px',
    px: '16px',
    pb: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  } satisfies SxProps<Theme>,

  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap'
  } satisfies SxProps<Theme>,

  cardTitle: { display: 'flex', alignItems: 'center', gap: '8px' } satisfies SxProps<Theme>,

  cardHeaderActions: { display: 'flex', alignItems: 'center', gap: '16px' } satisfies SxProps<Theme>,

  // The current default is disabled (there is no "no default" state), but it stays coloured —
  // greying the one checked box in the panel reads as unavailable rather than as the active choice.
  defaultCheckbox: {
    '&.Mui-checked.Mui-disabled': { color: 'var(--blue-500)' }
  } satisfies SxProps<Theme>,

  viewOnlyPill: {
    alignSelf: 'flex-start',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    height: '40px',
    px: '16px',
    borderRadius: '20px',
    backgroundColor: 'var(--blue-50)'
  } satisfies SxProps<Theme>,

  viewOnlyIcon: { color: 'var(--grey-700)', width: '24px', height: '24px' } satisfies SxProps<Theme>,

  columns: {
    display: 'grid',
    gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 344px) minmax(0, 277px)' },
    gap: '40px',
    px: '32px',
    py: '16px'
  } satisfies SxProps<Theme>,

  singleColumn: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '40px',
    px: '32px',
    py: '16px'
  } satisfies SxProps<Theme>,

  column: { display: 'flex', flexDirection: 'column', gap: '16px' } satisfies SxProps<Theme>,

  chips: { display: 'flex', flexWrap: 'wrap', gap: '16px' } satisfies SxProps<Theme>,

  chip: {
    color: 'var(--grey-900)',
    backgroundColor: 'var(--blue-300)',
    fontSize: '14px',
    fontFamily: 'Open Sans'
  } satisfies SxProps<Theme>,

  roleValue: { fontWeight: 600 } satisfies SxProps<Theme>,

  // Column tracks carry the gaps so the switches and labels land on Figma's x offsets. The editable
  // switch is 56px wide against the read-only 28px, so each mode needs its own track widths.
  functionGrid: {
    display: 'grid',
    gridTemplateColumns: '108px 72px 1fr',
    rowGap: '16px',
    alignItems: 'center'
  } satisfies SxProps<Theme>,

  functionGridReadOnly: {
    display: 'grid',
    gridTemplateColumns: '80px 44px 1fr',
    rowGap: '16px',
    alignItems: 'center'
  } satisfies SxProps<Theme>,

  functionToggle: {
    p: 0,
    borderRadius: 0,
    justifySelf: 'start',
    ':hover': { backgroundColor: 'transparent' }
  } satisfies SxProps<Theme>,

  functionSwitch: { display: 'flex', alignItems: 'stretch' } satisfies SxProps<Theme>,

  functionSwitchOff: { display: 'flex', alignItems: 'stretch', opacity: 0.6 } satisfies SxProps<Theme>,

  functionStateTrack: {
    width: '28px',
    height: '28px',
    backgroundColor: 'var(--blue-100)'
  } satisfies SxProps<Theme>,

  functionStateLocked: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--grey-300)',
    color: 'var(--grey-700)',
    fontSize: '10px',
    fontWeight: 700
  } satisfies SxProps<Theme>,

  functionStateOn: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--blue-500)',
    color: 'var(--white)',
    fontSize: '10px',
    fontWeight: 700
  } satisfies SxProps<Theme>,

  functionStateOff: {
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--grey-700)',
    color: 'var(--white)',
    fontSize: '10px',
    fontWeight: 700
  } satisfies SxProps<Theme>,

  functionHeader: { fontWeight: 700 } satisfies SxProps<Theme>,

  functionLabel: { display: 'flex', alignItems: 'center', gap: '4px' } satisfies SxProps<Theme>,

  helpIcon: { color: 'var(--grey-500)', width: '16px', height: '16px' } satisfies SxProps<Theme>,

  removeButton: { border: '1px solid var(--red-500)' } satisfies SxProps<Theme>,

  actions: { display: 'flex', gap: '16px', justifyContent: 'flex-end' } satisfies SxProps<Theme>
}
